import { useState } from 'react'
import { leases as leaseApi } from '@/api/endpoints'
import type { Lease } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Err } from '@/components/Err'
import { LEASE_TAG_LIMIT_RULE, leaseTagsOver } from '@/domain/lease'
import { usePastTags } from '@/hooks/usePastTags'
import { splitTags } from '@/domain/team'
import { cn } from '@/lib/utils'
import { CHIP, TAG } from './chips'

// VM 의 태그(F-95). 목록·관리자 상세·사용자 상세가 같은 칩을 쓰고, 고치는 창도 하나다 — 팀의 태그와 같은 칸(쉼표로 가른다)이다.
// 칩은 태그가 없으면 서지 않는다. 태그는 소유자의 낱말이라(F-109) 고치는 칩은 소유자에게만 부르는 쪽이 세운다(`mayTag`).
// 칸 아래에는 내가 전에 쓴 태그가 칩으로 선다(`PastTags`) — 누르면 칸에 더해진다. 같은 낱말을 매번 다시 치지 않게.

/** 태그 칩 한 줄. 없으면 아무것도 그리지 않는다. 줄이 좁으면 접힌다. */
export function VmTags({ tags, className }: { tags: readonly string[]; className?: string }) {
  if (tags.length === 0) return null
  return (
    <span className={cn('inline-flex flex-wrap gap-1', className)}>
      {tags.map((t) => (
        <span key={t} className={TAG}>
          {t}
        </span>
      ))}
    </span>
  )
}

/** 내가 전에 쓴 태그 가운데 칸에 없는 것. 누르면 그 태그를 칸의 끝에 더한다. 없으면 아무것도 그리지 않는다. */
export function PastTags({ raw, onChange, disabled = false }: { raw: string; onChange: (raw: string) => void; disabled?: boolean }) {
  const current = splitTags(raw)
  const past = usePastTags(current)
  if (past.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1" aria-label="이전 태그">
      {past.map((t) => (
        <button
          key={t}
          type="button"
          disabled={disabled}
          onClick={() => onChange([...current, t].join(', '))}
          className={cn(CHIP, 'border-dashed')}
        >
          + {t}
        </button>
      ))}
    </div>
  )
}

/** `태그 수정` 칩과 그 창. 저장하면 `onSaved` 로 부르는 쪽이 다시 읽는다. */
export function TagEditChip({ lease: l, onSaved, className = CHIP }: { lease: Lease; onSaved: () => void; className?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        태그 수정
      </button>
      <Dialog open={open} onOpenChange={(v) => (v ? undefined : setOpen(false))}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          {open ? (
            <TagForm
              lease={l}
              onClose={() => setOpen(false)}
              onDone={() => {
                setOpen(false)
                onSaved()
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function TagForm({ lease: l, onClose, onDone }: { lease: Lease; onClose: () => void; onDone: () => void }) {
  const [tags, setTags] = useState(l.tags.join(', '))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const over = leaseTagsOver(tags)
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (over) return
    setBusy(true)
    setError(null)
    try {
      await leaseApi.setTags(l.id, splitTags(tags))
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <DialogHeader>
        <DialogTitle>{l.name} 태그</DialogTitle>
      </DialogHeader>
      <div className="grid gap-1.5">
        <Label htmlFor="vm-tag-edit">태그</Label>
        <Input
          id="vm-tag-edit" value={tags} placeholder="수업, 연구실" disabled={busy}
          aria-invalid={over || undefined} onChange={(e) => setTags(e.target.value)}
        />
        {over ? <span className="text-[11px] text-danger-ink">{LEASE_TAG_LIMIT_RULE}</span> : null}
        <PastTags raw={tags} onChange={setTags} disabled={busy} />
      </div>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="submit" disabled={busy || over}>
          {busy ? '저장 중' : '저장'}
        </Button>
      </DialogFooter>
    </form>
  )
}
